import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import _ from 'lodash';

export function UserEditRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.UserEdit', {
		url: '/admin/users/:id/edit',
		controllerAs: 'vm',
		controller: UserEditCtrl,
		templateUrl: '/ng/hub/User/Edit/UserEditCtrl.html?' + window.BUILD,
		resolve: {
			$style: () => AssetManager.css('/ng/hub/User/Edit/UserEditCtrl.css'),
			$title: () => 'Edit User',
			user: ApiResourcify.endpoint('/users/:id'),
			editorAccess: ApiResourcify.endpoint('/users/me/access'),
			userAccess: ApiResourcify.endpoint('/users/:id/access'),
			roles: ApiResourcify.endpoint('/users/roles'),
		},
		permission: ['manage_users'],
		onExit: Taxonomy => {
			'ngInject';
			Taxonomy.clearUsers();
			Taxonomy.clearClientUserCount();
			Taxonomy.clearPublishers();
		},
	});
}

export class UserEditCtrl {
	constructor($filter, $q, Api, Identity, Auth, modal, notifications, user, editorAccess, userAccess, roles) {
		'ngInject';
		// save injections
		this.$q = $q;
		this.Api = Api;
		this.Identity = Identity;
		this.Auth = Auth;
		this.modal = modal;
		this.notifications = notifications;
		// handle injected data
		this.user = user;
		this.editorAccess = editorAccess;
		this.userAccess = userAccess;
		// setup options
		this.editorIsSuperadmin = this.Identity.isSuperadmin();
		this.canEditUser = this.editorIsSuperadmin || !this.user.is_superadmin;
		if (_.isEmpty(this.userAccess)) {
			// template will show that you do not have permission
			return;
		}
		this.generic_login_url = `${window.BASE_URL}/login`;
		this.loadPossibleAccounts();
		// setup filters
		this.filterByProp = $filter('shFilterByString');
		this.accountFilter = '';
		this.runFilters();
		// setup roles
		this.roleList = [{ id: '', name: '' }];
		this.roleLookup = [];
		roles.forEach(role => {
			if (role.role == 'observer') {
				return;
			}
			this.roleList.push({ id: role.role, name: role.label });
			this.roleLookup[role.role] = role.label;
		});
		if (this.user.sso && this.user.sso.diff_strategy) {
			const prohibitions = [];
			if (this.user.sso.diff_strategy.includes('toRemove')) {
				prohibitions.push('<b>adding</b> new hub access');
			}
			if (this.user.sso.diff_strategy.includes('toDowngrade')) {
				prohibitions.push('<b>upgrading</b> hub role');
			}
			if (this.user.sso.diff_strategy.includes('toUpgrade')) {
				prohibitions.push('<b>downgrading</b> hub role');
			}
			if (this.user.sso.diff_strategy.includes('toAdd')) {
				prohibitions.push('<b>removing</b> hub access');
			}
			const prohibit = prohibitions.join(', ');
			this.permWarning = `
				SSO rules prohibit ${prohibit}.<br><br>
				Such changes might be overwritten the next time a user logs in.<br><br>
				Please contact <a href="mailto:support@sharpr.com">support@sharpr.com</a> for more information.
			`;
		} else {
			this.permWarning = '';
		}
	}

	/*
		Account Access Rules:
		1. If you don't have access to the user's default hub, you can't change their default hub.
		2. If you have access to one account, it will be automatically expanded
		3. If you have access to multiple accounts, you see an input to filter accounts by name
		4. If you have access to 6+ hubs, you see an input to filter hubs by name
		5. If an intel leader edits an account manager, they will not be able to demote them or set a role in any of the hubs. It will just say "(Account Manager)"
		6. If a superadmin removes account admin status, they can re-add them to individual hubs
	 */
	loadPossibleAccounts() {
		let editorAccounts = _.cloneDeep(this.editorAccess.accounts);
		let userAccounts = _.cloneDeep(this.userAccess.accounts);
		this.canAccessDefaultHub = this.editorIsSuperadmin;
		// compile the data needed to render the list of hubs
		this.visibleAccounts = [];
		const userAccessLookup = {};
		const userAdminOfAccountIds = [];
		userAccounts.forEach(account => {
			account.hubs.forEach(hub => {
				if (account.is_admin) {
					userAdminOfAccountIds.push(account.id);
				}
				userAccessLookup[`${account.id}-${hub.id}`] = account.is_admin ? 'intel leader' : hub.role;
				let lastAcct = this.visibleAccounts[this.visibleAccounts.length - 1];
				if (!lastAcct || lastAcct.id != account.id) {
					lastAcct = {
						id: account.id,
						name: account.name,
						is_admin: account.is_admin,
						hubs: [],
					};
					this.visibleAccounts.push(lastAcct);
				}
				lastAcct.hubs.push({ ...hub });
			});
		});
		// get the data needed to render the edit checkboxes and selects
		editorAccounts.forEach(account => {
			account.hubFilter = '';
			account.isOpen = editorAccounts.length == 1; // rule 2
			if (userAdminOfAccountIds.indexOf(account.id) > -1) {
				account.is_admin = true;
				if (!this.editorIsSuperadmin) {
					account.hubs = []; // rule 5
					return;
				}
			} else {
				account.is_admin = false;
			}
			account.hubs.forEach(hub => {
				hub.role = account.is_admin ? '' : userAccessLookup[`${account.id}-${hub.id}`] || '';
				if (hub.id == this.user.default_hub_id) {
					this.canAccessDefaultHub = true; // rule 1
				}
			});
		});
		this.accounts = editorAccounts;
		// keep a record of original access
		this.originalAccounts = _.cloneDeep(this.accounts);
	}

	toggleMembershipFilter() {
		this.showMembership = !this.showMembership;
		if (this.showMembership) {
			this.memberOfAccounts = [];
			this.accounts.forEach(account => {
				account.memberOfHubs = account.hubs.filter(hub => hub.role);
				if (account.is_admin || account.memberOfHubs.length > 0) {
					this.memberOfAccounts.push(account);
					account.isOpen = true;
				}
			});
		}
		this.runFilters();
	}

	runFilters() {
		this.filterByText(this.showMembership ? this.memberOfAccounts : this.accounts);
	}

	filterByText(accounts) {
		if (this.accountFilter === '') {
			this.filteredAccounts = accounts;
		} else {
			this.filteredAccounts = this.filterByProp(accounts, 'name', this.accountFilter);
		}
		this.filteredAccounts.forEach(account => {
			const allHubs = this.showMembership ? account.memberOfHubs : account.hubs;
			if (account.hubFilter === '') {
				account.filteredHubs = allHubs;
			} else {
				account.filteredHubs = this.filterByProp(allHubs, 'name', account.hubFilter);
			}
		});
	}

	async loadPreview() {
		const { notes, data, names } = this._getSaveData();
		const hasAnyChanges = _.toArray(data).some(arr => arr && arr.length);

		if (!hasAnyChanges) {
			const noteString = notes.map(note => _.escape(note)).join('<br>');
			const noteBr = noteString ? '<br>' : '';
			this.modal.alert({ escapedHtml: noteString + noteBr + 'No changes to save.' });
		} else {
			this.loadingPreview = true;
			try {
				let [emailProviders, emailProvider, preview] = await Promise.all([
					this.Api.get('/emails/providers'),
					this.Api.get('/emails/provider', { recipient: this.user.email }),
					this.Api.post(`/users/${this.user.id}/access/preview`, data),
				]);

				this.emailProviders = emailProviders;
				this.emailProvider = emailProvider.id;
				this.preview = {
					email: preview.email, // htmlContent, to, options
					...names,
				};
				this.showPreview = true;
				this.loadingPreview = false;
				this.previewNotes = notes;
			} catch (error) {
				this.modal.alert({ escapedHtml: `<p>We ran into an error while fetching changes</p>` });
			}
		}
	}

	saveAccess() {
		const { data } = this._getSaveData();
		data.send_email = this.sendEmail;
		data.email_note = this.emailNote;
		data.email_provider = this.emailProvider;
		this.showPreview = false;
		this.editingAccess = false;
		// this.resetChangeTracking();
		this.Api.post(`/users/${this.user.id}/access`, data).then(() => {
			const message = 'Changes saved.' + (data.send_email ? ' User has been emailed.' : '');
			this.notifications.showSuccess({ message });
			this._reloadAccess();
		});
	}

	revokeSuperadmin() {
		const msg = 'Are you sure you want to revoke superadmin abilities?';
		this.modal.confirm({ text: msg }).then(() => {
			this._setSuperadmin(false);
		});
	}

	makeSuperadmin() {
		const msg = 'Are you sure you want to grant superadmin abilities?';
		this.modal.confirm({ text: msg }).then(() => {
			this._setSuperadmin(true);
		});
	}

	deleteUser() {
		const msg = 'Are you sure you want to delete this user?';
		this.modal.confirm({ text: msg }).then(() => {
			this._setActive(false);
		});
	}

	undeleteUser() {
		const msg = 'Are you sure you want to reactivate this user?<br><br>A notification email will <b>not</b> be sent.';
		this.modal.confirm({ escapedHtml: msg }).then(() => {
			this._setActive(true);
		});
	}

	sendResetLink() {
		this.Auth.sendResetLink(this.user.email).then(resp => {
			const className = resp.success ? '' : 'text-danger';
			this.modal.alert({ escapedHtml: `<span class="${className}">${resp.message}</span>` });
		});
	}

	impersonateUser() {
		this.Api.get(`/users/${this.user.id}/impersonate?referer=${encodeURIComponent(window.location.href)}`).then(
			response => {
				localStorage.setItem('jwt', JSON.stringify({ payload: response.payload, success: true, token: response.token }));
				window.location.href = `${response.url}`;
			}
		);
	}

	viewAccessHistory() {
		this.modal
			.state('hub.loggedin.UserAccessHistory', {
				params: { id: this.user.id },
				heading: `User Access History for ${this.user.fname_preferred || this.user.fname} ${
					this.user.lname_preferred || this.user.lname
				}`,
				height: 'full',
				width: 1200,
			})
			.then(
				() => {},
				() => {}
			);
	}

	openAccessEditor() {
		const open = () => (this.editingAccess = true);
		if (this.permWarning) {
			this.modal
				.confirm({
					escapedHtml: this.permWarning,
					heading: 'Warning',
					confirmText: 'Continue',
					cancelText: 'Cancel',
				})
				.then(open);
		} else {
			open();
		}
	}

	_getSaveData() {
		// create diff skeleton
		const changes = {
			add_hubs: [],
			remove_hubs: [],
			update_hubs: [],
			add_accounts: [],
			remove_accounts: [],
		};
		const roleChanges = [];
		// calculate diff
		this.accounts.forEach((acct, i) => {
			const origAcct = this.originalAccounts[i];
			if (acct.is_admin && !origAcct.is_admin) {
				changes.add_accounts.push(acct);
			} else if (!acct.is_admin && origAcct.is_admin) {
				changes.remove_accounts.push(acct);
			}
			acct.hubs.forEach((hub, j) => {
				const origHub = origAcct.hubs[j];
				if (hub.role && !origHub.role) {
					changes.add_hubs.push(hub);
				} else if (!hub.role && origHub.role) {
					changes.remove_hubs.push(hub);
				} else if (hub.role != origHub.role) {
					changes.update_hubs.push(hub);
					roleChanges.push({
						name: hub.name,
						was: origHub.role,
						now: hub.role,
					});
				}
			});
		});
		return {
			data: {
				add_hubs: changes.add_hubs,
				update_hubs: changes.update_hubs,
				remove_hubs: _.map(changes.remove_hubs, 'id'),
				add_accounts: _.map(changes.add_accounts, 'id'),
				remove_accounts: _.map(changes.remove_accounts, 'id'),
				can_approve_content: changes.can_approve_content,
			},
			names: {
				addHubs: this._formatNames(changes.add_hubs),
				roleChanges: this._formatRoleChanges(roleChanges),
				removeHubs: this._formatNames(changes.remove_hubs),
				addAccounts: this._formatNames(changes.add_accounts),
				removeAccounts: this._formatNames(changes.remove_accounts),
			},
			notes: [],
		};
	}

	_formatNames(collection) {
		return _.map(collection, 'name').join(', ');
	}

	_formatRoleChanges(collection) {
		const getChangeText = change => {
			const was = this.roleLookup[change.was];
			const now = this.roleLookup[change.now];
			return `${change.name} (${was} → ${now})`;
		};
		return _.map(collection, getChangeText).join(', ');
	}

	_setSuperadmin(onOrOff) {
		this.Api.patch(`/users/${this.user.id}`, { is_superadmin: onOrOff }).then(() => {
			this.user.is_superadmin = onOrOff;
			this._reloadAccess();
		});
	}

	_setActive(activeOrInactive) {
		this.user.is_active = activeOrInactive;
		this.Api.patch(`/users/${this.user.id}`, { is_active: activeOrInactive });
	}

	_reloadAccess() {
		this.Api.get(`/users/${this.user.id}/access`).then(access => {
			this.userAccess = access;
			this.loadPossibleAccounts();
		});
	}
}
